import * as TabsPrimitive from "@radix-ui/react-tabs";
import * as React from "react";

import { cn } from "../utils";

const Tabs = TabsPrimitive.Root;

// useLayoutEffect warns "does nothing on the server" during SSR (Tabs is
// consumed by website-features under Next). Fall back to useEffect on the
// server; in the browser it stays a layout effect so the pill is measured and
// positioned before paint (no flicker).
const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? React.useLayoutEffect : React.useEffect;

type PillRect = { left: number; top: number; width: number; height: number };

const TabsList = ({
  className,
  children,
  ref,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List>) => {
  const listRef = React.useRef<HTMLDivElement | null>(null);
  // The active "pill" is a single shared element that slides to sit behind the
  // active trigger, rather than each trigger painting its own background (which
  // would snap rather than slide). `null` until we've measured an active tab.
  const [pill, setPill] = React.useState<PillRect | null>(null);

  // Merge the forwarded ref with our internal measuring ref.
  const setRefs = React.useCallback(
    (node: HTMLDivElement | null) => {
      listRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) ref.current = node;
    },
    [ref],
  );

  useIsomorphicLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;

    const update = () => {
      // `aria-selected`, NOT `data-state="active"`. Radix Tabs sets both on a trigger, but a
      // trigger wrapped in a Radix Tooltip via `asChild` gets its `data-state` overwritten by
      // the Tooltip's own open/closed state on the merged node — so this query found nothing,
      // setPill(null) fired, and the pill silently vanished for that consumer. Measured in
      // admin's application-state tab bar, where every trigger but one carries a
      // ResponsiveTooltip: the active tab reported data-state="closed". `aria-selected`
      // survives the merge.
      const active = list.querySelector<HTMLElement>('[aria-selected="true"]');
      if (!active) {
        setPill(null);
        return;
      }
      setPill({
        left: active.offsetLeft,
        top: active.offsetTop,
        width: active.offsetWidth,
        height: active.offsetHeight,
      });
    };

    update();

    // `aria-selected` AND `data-state`, for the same reason the query above uses aria-selected.
    // Watching data-state alone strands the pill for any consumer that wraps its triggers in a
    // Radix Tooltip: the Tooltip owns data-state on the merged node, so switching between two
    // wrapped triggers changes NO data-state anywhere in the list and the callback never runs.
    // Measured in admin's application-state bar — clicking from Created to Advice moved the
    // selection to offsetLeft 377 while the pill stayed behind at 253. It only looked correct
    // there when one of the two tabs involved was the single trigger without a tooltip, whose
    // data-state is still Radix's. data-state stays in the filter for consumers that don't wrap.
    const mutationObserver = new MutationObserver(update);
    mutationObserver.observe(list, {
      attributes: true,
      attributeFilter: ["data-state", "aria-selected"],
      subtree: true,
    });
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(list);

    return () => {
      mutationObserver.disconnect();
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <TabsPrimitive.List
      ref={setRefs}
      className={cn(
        // shadcn-style segmented control: a muted track holding the triggers.
        // `relative` anchors the sliding active pill. w-fit keeps it
        // content-sized (pass w-full to span); flex-wrap lets it break onto
        // multiple rows when there are too many tabs to fit on one line.
        "relative inline-flex w-fit max-w-full flex-wrap items-center justify-center gap-1 rounded-lg bg-gray-100 p-1 text-gray-600",
        className,
      )}
      {...props}
    >
      <span
        aria-hidden
        // A plain white pill on the gray-100 track read as too subtle to tell "which tab is
        // active" at a glance (KREA-4503) — the ring + stronger shadow give the active tab a
        // visible edge instead of relying on the (already-present) label color change alone.
        className="pointer-events-none absolute z-0 rounded-md bg-white shadow-md ring-1 ring-primary-200 transition-all duration-200 ease-out motion-reduce:transition-none"
        style={
          pill
            ? {
                left: pill.left,
                top: pill.top,
                width: pill.width,
                height: pill.height,
                opacity: 1,
              }
            : { opacity: 0 }
        }
      />
      {children}
    </TabsPrimitive.List>
  );
};
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = ({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      // relative + z-10 keeps the label above the shared sliding pill in TabsList
      "relative z-10 inline-flex flex-1 cursor-pointer items-center justify-center whitespace-nowrap rounded-md border border-transparent px-3 py-1 text-sm font-semibold ring-offset-white transition-all",
      "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
      // Every label is primary so each tab reads as clickable; an inactive tab
      // shows hover as a tint (the active one already sits on the pill). Keyed
      // on aria-selected, not data-state, for the same reason as the pill query
      // above: a Tooltip-wrapped trigger loses its Tabs data-state.
      "text-primary not-aria-selected:hover:bg-primary-100",
      className,
    )}
    {...props}
  />
);
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = ({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      // blur-and-fade the panel in each time its tab becomes active
      // (fade-blur-in keyframe defined in theme.css); disabled for users who
      // prefer reduced motion — the panel just appears
      "mt-2 outline-hidden data-[state=active]:animate-fade-blur-in motion-reduce:animate-none",
      className,
    )}
    {...props}
  />
);
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsContent, TabsList, TabsTrigger };
